import { Alert, AlertTitle, Box, Button, Skeleton, Snackbar, Stack } from '@mui/material';
import { ActionValues, ReturnProcessTab } from './ReturnProcessTab';
import { useReturnProcess } from './useReturnProcess';

interface Props {
  cmsId?: string;
}

export function ReturnProcessPanel({ cmsId }: Props) {
  const {
    state,
    documents,
    hasProcess,
    isInitialLoading,
    isError,
    actionError,
    dismissActionError,
    refetch,
    sendAction,
    isTransitioning,
  } = useReturnProcess(cmsId);

  if (isInitialLoading) {
    return (
      <Stack gap={2} mt={2} width="100%" data-testid="return-process-loading">
        <Skeleton variant="rounded" width="100%" height={56} />
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} width="100%" alignItems="flex-start">
          <Skeleton variant="rounded" sx={{ flex: '1 1 0', minWidth: 0 }} height={320} />
          <Skeleton variant="rounded" sx={{ flex: '0 0 320px', maxWidth: '100%' }} height={220} />
        </Stack>
      </Stack>
    );
  }

  // A failed refresh must not replace a state that already loaded.
  if (isError && !hasProcess) {
    return (
      <Box mt={2}>
        <Alert
          severity="error"
          data-testid="return-process-load-error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Erneut versuchen
            </Button>
          }
        >
          <AlertTitle>Der Rückläufervorgang konnte nicht geladen werden</AlertTitle>
          Der Vorgang ist gerade nicht erreichbar. Bitte später erneut versuchen; die bisherigen
          Eingaben gehen nicht verloren.
        </Alert>
      </Box>
    );
  }

  if (!hasProcess || !state) {
    return (
      <Box mt={2}>
        <Alert severity="info" data-testid="return-process-none">
          Für diesen Vertrag läuft kein Rückläufervorgang.
        </Alert>
      </Box>
    );
  }

  const handleAction = (actionKey: string, values: ActionValues) => {
    const correlationKey = state.current?.correlationKey;
    if (!correlationKey) return;
    sendAction({ correlationKey, action: actionKey, data: values });
  };

  return (
    <>
      <ReturnProcessTab
        state={state}
        documents={documents}
        onAction={handleAction}
        isTransitioning={isTransitioning}
        isStale={isError}
      />

      <Snackbar
        open={actionError}
        autoHideDuration={6000}
        onClose={dismissActionError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={dismissActionError}
          data-testid="return-process-action-error"
        >
          Die Aktion konnte nicht ausgeführt werden. Bitte erneut versuchen.
        </Alert>
      </Snackbar>
    </>
  );
}
